import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AppSnackbar } from '@sierralabs/react-material-ui';

import { FetchError } from '../../common/api/api';
import { useFindLinkedEntities } from './jira-server.hooks';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import {
  getProductEntityIdPrefix,
  getProductEntityName
} from '../products/Product.config';
import { HazardBoardColumnEntity } from '../hazards/Hazard.interface';
import { getHazardEntityPrefix } from '../hazards/Hazard.hooks';
import { useJiraConnectContext } from '../jira-connect/JiraConnect.context';
import {
  createServletUrl,
  useFindJiraConnect
} from '../jira-connect/JiraConnect.hooks';
import { useJiraServerContext } from './jira-server.context';
import { JiraLinkedEntity } from './jira-server.interface';
import environment from '../../environments';

const CapitalizedText = styled.span`
  text-transform: capitalize;
`;

const JiraStyledLink = styled.a`
  text-decoration: none;
  transition: color 0.25s cubic-bezier(0.075, 0.82, 0.165, 1);
  &.disabled {
    color: #0052cc88;
    cursor: not-allowed;
  }
  &:not(.disabled) {
    color: #0052cc;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const getTypeLabel = (type: WorkflowEntity) => {
  switch (type) {
    case WorkflowEntity.Action:
    case WorkflowEntity.Cause:
    case WorkflowEntity.Control:
      return `Hazard ${type}`;
    case WorkflowEntity.UserNeed:
      return getProductEntityName('userNeed');
    case WorkflowEntity.DesignInput:
      return getProductEntityName('designInput');
    case WorkflowEntity.DesignOutput:
      return getProductEntityName('designOutput');
    case WorkflowEntity.DesignSpec:
      return getProductEntityName('designSpec');
    case WorkflowEntity.DesignValidation:
      return getProductEntityName('validation');
    case WorkflowEntity.DesignVerification:
      return getProductEntityName('verification');
    default:
      return type;
  }
};

const getEntityId = (type: WorkflowEntity, id: number) => {
  switch (type) {
    case WorkflowEntity.Action:
      return `${getHazardEntityPrefix(HazardBoardColumnEntity.Action)}${id}`;
    case WorkflowEntity.Cause:
      return `${getHazardEntityPrefix(HazardBoardColumnEntity.Cause)}${id}`;
    case WorkflowEntity.Control:
      return `${getHazardEntityPrefix(HazardBoardColumnEntity.Control)}${id}`;
    case WorkflowEntity.UserNeed:
      return `${getProductEntityIdPrefix('userNeed')}-${id}`;
    case WorkflowEntity.DesignInput:
      return `${getProductEntityIdPrefix('designInput')}-${id}`;
    case WorkflowEntity.DesignOutput:
      return `${getProductEntityIdPrefix('designOutput')}-${id}`;
    case WorkflowEntity.DesignSpec:
      return `${getProductEntityIdPrefix('designSpec')}-${id}`;
    case WorkflowEntity.DesignValidation:
      return `${getProductEntityIdPrefix('validation')}-${id}`;
    case WorkflowEntity.DesignVerification:
      return `${getProductEntityIdPrefix('verification')}-${id}`;
    default:
      return `${type.toUpperCase()}-${id}`;
  }
};

const getEntityPath = (type: WorkflowEntity, entity: JiraLinkedEntity) => {
  switch (type) {
    case WorkflowEntity.Action:
      return `portal/hazards/${entity.hazardId}/actions/${entity.id}`;
    case WorkflowEntity.Cause:
      return `portal/hazards/${entity.hazardId}/causes/${entity.id}`;
    case WorkflowEntity.Control:
      return `portal/hazards/${entity.hazardId}/controls/${entity.id}`;
    case WorkflowEntity.UserNeed:
      return `portal/products/${entity.productId}/user-needs/${entity.id}`;
    case WorkflowEntity.DesignInput:
      return `portal/products/${entity.productId}/design-inputs/${entity.id}`;
    case WorkflowEntity.DesignOutput:
      return `portal/products/${entity.productId}/design-outputs/${entity.id}`;
    case WorkflowEntity.DesignSpec:
      return `portal/products/${entity.productId}/design-specs/${entity.id}`;
    case WorkflowEntity.DesignValidation:
      return `portal/products/${entity.productId}/validations/${entity.id}`;
    case WorkflowEntity.DesignVerification:
      return `portal/products/${entity.productId}/verifications/${entity.id}`;
    default:
      return `portal/${type}s/${entity.id}`;
  }
};

const EntityLink: React.FC<{
  type: WorkflowEntity;
  entity: JiraLinkedEntity;
  onError?: (error: FetchError) => void;
}> = ({ type, entity, onError }) => {
  const { isAvailable } = useJiraConnectContext();
  const { isJiraServer } = useJiraServerContext();
  const [{ data: jiraConnect, error }, findJiraConnect] = useFindJiraConnect();

  const pagePath: string = useMemo(() => {
    return getEntityPath(type, entity);
  }, [type, entity]);

  const url: URL | undefined = useMemo(() => {
    if (isJiraServer) {
      const jiraBaseUrl = environment.jiraUrl || 'http://localhost:2990/jira';
      const url: URL = new URL(
        jiraBaseUrl.concat('/plugins/servlet/sierra-qms')
      );
      url.searchParams.set('page', pagePath);
      return url;
    } else if (isAvailable && jiraConnect) {
      // add slash to pagePath
      const path: string = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
      return createServletUrl(jiraConnect, 'sierra-qms-products', path);
    }
  }, [pagePath, jiraConnect, isAvailable, isJiraServer]);

  useEffect(() => {
    findJiraConnect();
  }, [findJiraConnect]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <JiraStyledLink
      target='_blank'
      // disable link if no url
      href={url ? url.href : undefined}
      className={!url ? 'disabled' : undefined}
    >
      {getEntityId(type, entity.id)}
    </JiraStyledLink>
  );
};

const JiraLinkedEntities: React.FC = () => {
  const { jiraKey } = useParams<{ jiraKey: string }>();
  const [error, setError] = useState<FetchError | undefined>();
  const { data, isLoading, error: findError, get } = useFindLinkedEntities(
    jiraKey
  );
  const tableRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    setError(findError);
  }, [findError]);

  useEffect(() => {
    if (tableRef.current) {
      const { height } = tableRef.current.getBoundingClientRect();
      console.debug('sending SetHeight event', height);
      window.parent.postMessage({ type: 'SetHeight', params: height }, '*');
    }
    // notify plugin when table rows change or data changes to adjust iframe height
  }, [tableRef.current?.rows, data]);

  return (
    <>
      <TableContainer>
        <Table ref={tableRef}>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {data?.map(task => {
              return task.entities?.map(entity => {
                return (
                  <TableRow key={`${task.type}_${entity.id}`}>
                    <TableCell>
                      <CapitalizedText>
                        {getTypeLabel(task.type as WorkflowEntity)}
                      </CapitalizedText>
                    </TableCell>
                    <TableCell>
                      <EntityLink
                        type={task.type as WorkflowEntity}
                        entity={entity}
                        onError={setError}
                      />
                    </TableCell>
                    <TableCell>{entity.title}</TableCell>
                    <TableCell>
                      <TaskStatusLabel task={entity.task} />
                    </TableCell>
                  </TableRow>
                );
              });
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AppSnackbar open={!!error} severity='error'>
        {error?.message}
      </AppSnackbar>
    </>
  );
};
export default JiraLinkedEntities;
